import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RouteConfig, useNavigate } from '.';
import { fromAccounts, fromAccountUsers, fromAuth, fromSubscription, useAppDispatch } from '../store';
import { useQuery } from './useQuery';
import { usePermission } from '../permission';
import { hasPermission } from '../utils/PermissionCheck';
import routes, {
  accountSelection,
  orderConfirmation,
  paymentStatus,
  plan,
  subscription,
  subscriptionManagement,
} from '../layout/routes';
import { RouteVersion, SubscriptionSource } from '../models';
import { useLaunchDarkly } from '../launchdarkly';

// Routes accessible only during new account checkout flow (no paid subscriptions)
const NEW_ACCOUNT_ALLOWED_PATHS = [plan.path, orderConfirmation.path, paymentStatus.path];

export const useAccountAuthenticate = (
  {
    isRootOrAdminAccessible,
    accessibleRoles,
    resource,
    actions,
  }: RouteConfig,
) => {
  const user = useSelector(fromAuth.selectUser);
  const { get, queryDictionary } = useQuery();
  const { permissionMapping } = usePermission();
  const { flags, changeAccount } = useLaunchDarkly();
  const accountId = get('accountId');
  const spEnabled = get('sp') === 'true';
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { navigate, replaceQuery } = useNavigate();
  const isRootOrAdmin = useSelector(fromAuth.selectIsRootOrAdmin);
  const [isAccountAuthenticated, setIsAccountAuthenticated] = useState(false);
  const [redirectToNotFound, setRedirectToNotFound] = useState(false);

  useEffect(() => {
    const verifyStarterPackage = async (accountId: number) => {
      const getUrlPath = (
        currentRoute: RouteConfig | undefined,
        isOptedInStarterPackage: boolean,
      ): string => {
        // in case of account is opted in starter package
        if (isOptedInStarterPackage) {
          // if current route is new v2 route, navigate to replace path if available
          // else navigate to subscription management page
          if (currentRoute?.version === RouteVersion.V2) {
            return currentRoute.replacePath ?? subscriptionManagement.path;
          }
          return currentRoute?.path ?? subscriptionManagement.path;
        }

        // in case of account is not opted in starter package
        // and current route is v1 route, navigate to replace path if available
        // else navigate to new subscription page
        if (currentRoute?.version === RouteVersion.V1) {
          return currentRoute.replacePath ?? subscription.path;
        }

        return currentRoute?.path ?? subscription.path;
      };

      await dispatch(fromAccounts.doCheckStarterPackage({ accountId, spEnabled }))
        .then(unwrapResult)
        .then(isOptedInStarterPackage => {
          const currentRoute = routes.find(route => location.pathname === route.path);
          navigate(
            getUrlPath(currentRoute, isOptedInStarterPackage),
            replaceQuery(queryDictionary()),
          );
        });
    };

    const verifyUserAccountRoleAndPermission = async (
      userId: number,
      accountId: number,
    ): Promise<boolean> => {
      const currentAccountUser = await dispatch(fromAccountUsers.doGetByUserIdAndAccountId({
        userId,
        accountId,
      })).then(unwrapResult);

      const accountUser = currentAccountUser.total > 0
        ? currentAccountUser.data.at(0) : undefined;

      if ((isRootOrAdminAccessible && isRootOrAdmin)
        || (accountUser
          && (accessibleRoles === undefined || accessibleRoles?.includes(accountUser.role)))
        || hasPermission(permissionMapping, resource, actions)
      ) {
        dispatch(fromAccounts.doSetSelectedAccount(accountUser?.account));
        return true;
      }

      setRedirectToNotFound(true);
      return false;
    };

    const verifyNewAccountFlow = async (accountId: number) => {
      try {
        if (changeAccount) {
          await changeAccount(accountId);
        }

        const result = await dispatch(fromSubscription.doFetchSubscriptions({ accountId }))
          .then(unwrapResult);

        const hasPaidSubscriptions = result.subscriptionResponse.some(
          sub => sub.source === SubscriptionSource.RECURLY,
        );

        const currentPath = location.pathname;

        if (flags?.truePlatformEnabled && !hasPaidSubscriptions) {
          // New account - only allow plan and order-confirmation routes
          if (!NEW_ACCOUNT_ALLOWED_PATHS.includes(currentPath)) {
            navigate(plan.path, replaceQuery(queryDictionary()));
            return;
          }
        }
      } catch {
        // If subscription fetch fails, allow access to proceed
      }

      setIsAccountAuthenticated(true);
    };

    const fetchInfo = async () => {
      // if accountId is not provided, redirect to account selection page
      if (accountId === null) {
        navigate(accountSelection.path, replaceQuery(queryDictionary()));
        return;
      }

      if (user) {
        setIsAccountAuthenticated(false);
        if (Number.isNaN(Number(accountId))) {
          // redirect to notfound page in case of an invalid account id
          setRedirectToNotFound(true);
          return;
        }

        // verify if the accountId is opted in starter package
        // redirect to g2 subscription management page
        await verifyStarterPackage(Number(accountId));

        // fetch account user to verify role and permission
        const isAuthorized = await verifyUserAccountRoleAndPermission(user?.id, Number(accountId));

        // verify new account flow - check subscriptions and restrict routes
        if (isAuthorized) {
          await verifyNewAccountFlow(Number(accountId));
        }
      } else {
        setRedirectToNotFound(true);
      }
    };
    fetchInfo().catch(() => {
      // do nothing
    });
  }, [
    user,
    accountId,
    accessibleRoles,
    permissionMapping,
    resource,
    actions,
    isRootOrAdmin,
    isRootOrAdminAccessible,
  ]);

  return {
    isAccountAuthenticated,
    redirectToNotFound,
  };
};
